/* PlanApprovalHero.jsx — the doctor's full plan-review surface, shown
 * inside the Treatment Plan tab when a CoE (Planning Service) case has
 * been sent back for approval.
 *
 * iter98.99aw — REDESIGN to match the doctor-provided spec mockup.
 *   "Check this for the doctor portal — Treatment Plan tab. This is
 *    where the plan from the planner appears for the doctor to review
 *    and approve or request revision. Keep your theme colors."
 *
 * The previous iter98.99av storytelling layout (2-up grid, quote-bubble,
 * aux-chip strip) is replaced with the spec's structured-section pattern:
 *
 *   1. ⌜ Tier headline (slim) — tier name + cost · duration · ETA
 *        + a "Jump to approval" CTA that scroll-anchors to the bottom
 *        decision section in PlanIterationsStack.
 *   2. ⌜ Plan Summary — at-a-glance row table
 *   3. ⌜ Bite Metrics — initial vs target OB/OJ + total IPR
 *   4. ⌜ Clinical Protocol — text fields (attachments / IPR / elastics /
 *        planner notes / overcorrection / aux other notes)
 *   5. ⌜ Per-Tooth Plan — TABBED: Movements · Attachments · Precision
 *        Cuts · Bite Ramps. Each tab renders an FDI dental chart with
 *        coloured dots per tooth + an authored-summary list below.
 *   6. ⌜ Aligners & Wear Schedule — default cadence + per-stage overrides
 *   7. ⌜ Bite Ramps — (only when plan.bite_ramps?.enabled) staging detail
 *   8. ⌜ Retention Plan — retainer type · schedule · recall #1/#2
 *   9. ⌜ Payment & Offers — kept at the BOTTOM (per iter98.99av)
 *
 * Below this hero sits PlanIterationsStack with "Your decision on this
 * plan" (Approve / Request changes). The scroll-to button on the tier
 * card jumps to that section's anchor.
 *
 * Theme: uses our Apollo cyan-teal primary tokens (text-primary,
 * bg-primary, border-primary) — NOT the spec's hard-coded #1D9E75.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    ArrowRight,
    CaretDown, ArrowUpRight,
    Quotes, ArrowDown, ArrowsHorizontal, ArrowsClockwise,
    Bookmark, Scissors, WaveSine, Compass, Anchor, Pill,
    ClipboardText, ListChecks, ChartLine, Tooth as ToothIcon,
    FileText,
} from "@phosphor-icons/react";
import api from "@/lib/api";


/* ────────────────────────── utils ────────────────────────── */

function fmtINR(n) {
    if (n == null || n === "") return "—";
    const v = Number(n);
    if (!Number.isFinite(v)) return "—";
    return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

function fmtDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    } catch { return null; }
}

function computeEta(plan) {
    const weeks = Number(plan?.estimated_duration_weeks) || 0;
    if (!weeks) return null;
    const startIso = plan?.start_date;
    const start = startIso ? new Date(startIso) : new Date();
    if (Number.isNaN(start.getTime())) return null;
    const eta = new Date(start);
    eta.setDate(eta.getDate() + weeks * 7);
    return fmtDate(eta.toISOString());
}

function humanize(s) {
    if (!s) return "";
    return String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* iter98.99cr — Maps the planner-side refinement_expected_reason slug
   back to its display label (kept in sync with REFINEMENT_REASONS in
   PlanTimelineSection.jsx). */
const REFINEMENT_REASON_LABELS = {
    extraction: "Extraction case",
    class_iii: "Class III malocclusion",
    open_bite: "Open bite",
    deep_bite: "Deep bite",
    growth: "Skeletal growth / paediatric",
    other: "Other",
};
function humaniseRefinementReason(slug) {
    if (!slug) return "";
    return REFINEMENT_REASON_LABELS[slug] || humanize(slug);
}

function deltaTone(delta) {
    if (delta == null) return "neutral";
    if (delta < 0) return "improving";
    if (delta === 0) return "same";
    return "worsening";
}


/* ───────────────────── tooth-chart helpers ───────────────────── */

/* FDI quadrants. Right-side teeth are rendered as 18→11 / 48→41 (so the
   chart looks like the patient's mouth from the operator's view: the
   patient's RIGHT is on OUR left). */
const UPPER_RIGHT = ["18", "17", "16", "15", "14", "13", "12", "11"];
const UPPER_LEFT  = ["21", "22", "23", "24", "25", "26", "27", "28"];
const LOWER_RIGHT = ["48", "47", "46", "45", "44", "43", "42", "41"];
const LOWER_LEFT  = ["31", "32", "33", "34", "35", "36", "37", "38"];

const TAB_THEMES = {
    movements:   { dot: "bg-primary",      ring: "ring-primary/40",       tint: "bg-primary/10",       text: "text-primary",       label: "Movements",       icon: Compass     },
    attachments: { dot: "bg-sky-500",      ring: "ring-sky-300",          tint: "bg-sky-50",           text: "text-sky-700",       label: "Attachments",     icon: Bookmark    },
    cuts:        { dot: "bg-rose-500",     ring: "ring-rose-300",         tint: "bg-rose-50",          text: "text-rose-700",      label: "Precision cuts",  icon: Scissors    },
    biteramps:   { dot: "bg-amber-500",    ring: "ring-amber-300",        tint: "bg-amber-50",         text: "text-amber-800",     label: "Bite ramps",      icon: WaveSine    },
};

/* iter98.99ax — Patient-friendly tooth labelling.
 *
 * In the default "FDI" mode, every tooth tile shows its two-digit FDI
 * number ("13", "26", …) — what every clinician reads natively.
 *
 * In "Patient view" mode, the tile shows a 3-char anatomical shorthand
 * (UR3, UL6, …) and tooltips/authored-list rows surface the full
 * anatomy ("Upper right canine"). This turns the per-tooth chart into a
 * chair-side patient-education asset the doctor can flip on while
 * walking the patient through the plan.
 *
 * Quadrant code map:
 *   FDI 1x → UR  (Upper right)   FDI 2x → UL  (Upper left)
 *   FDI 3x → LL  (Lower left)    FDI 4x → LR  (Lower right)
 *
 * Position 1-8 maps to standard tooth anatomy (central → wisdom).
 */
const TOOTH_POSITION_NAME = {
    1: "central incisor",
    2: "lateral incisor",
    3: "canine",
    4: "1st premolar",
    5: "2nd premolar",
    6: "1st molar",
    7: "2nd molar",
    8: "wisdom tooth",
};
const QUADRANT_CODE = { "1": "UR", "2": "UL", "3": "LL", "4": "LR" };
const QUADRANT_NAME = { "1": "Upper right", "2": "Upper left", "3": "Lower left", "4": "Lower right" };

function fdiToShort(fdi) {
    const s = String(fdi || "").trim();
    if (s.length !== 2) return s;
    const q = QUADRANT_CODE[s[0]];
    return q ? `${q}${s[1]}` : s;
}

function fdiToAnatomy(fdi) {
    const s = String(fdi || "").trim();
    if (s.length !== 2) return s;
    const quad = QUADRANT_NAME[s[0]];
    const pos = TOOTH_POSITION_NAME[Number(s[1])];
    if (!quad || !pos) return s;
    return `${quad} ${pos}`;
}

function toothTileLabel(fdi, mode) {
    return mode === "patient" ? fdiToShort(fdi) : fdi;
}

function toothHeading(fdi, mode) {
    return mode === "patient"
        ? `${fdiToAnatomy(fdi)} (FDI ${fdi})`
        : `FDI ${fdi}`;
}

function toothTitle(fdi, mode, active) {
    const base = mode === "patient"
        ? `${fdiToAnatomy(fdi)} — FDI ${fdi}`
        : `FDI ${fdi}`;
    return active ? `${base} · authored` : base;
}


/* ───────────────────── default export ───────────────────── */

export default function PlanApprovalHero({ c, onChange, setupFiles, documentMode = false }) {
    const planning = c?.planning_mode || "coe";
    const status = c?.status;
    const inApproval = status === "sent_for_approval" || status === "ready_for_review";
    const pricing = c?.pricing || {};
    const plan = c?.plan || {};

    /* Per-tooth lookups for the Per-Tooth Plan tabs. Each lookup is a
       Map<FDI, ToothEntry> so chart dots + authored summary share the
       same source. */
    const movementsByTooth = useMemo(() => {
        const m = new Map();
        (plan.tooth_movements_plan || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan.tooth_movements_plan]);

    const attachmentsByTooth = useMemo(() => {
        const m = new Map();
        (plan.attachments_plan || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan.attachments_plan]);

    const cutsByTooth = useMemo(() => {
        const m = new Map();
        (plan.precision_cuts || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan.precision_cuts]);

    /* Bite-ramp teeth: prefer the structured `bite_ramps.teeth` list;
       fall back to standard upper-anterior set when only `enabled+position`
       is authored. */
    const biteRampTeeth = useMemo(() => {
        const br = plan.bite_ramps || {};
        if (!br.enabled) return new Set();
        if (Array.isArray(br.teeth) && br.teeth.length > 0) return new Set(br.teeth.map(String));
        const pos = br.position || "upper_anterior";
        if (pos === "upper_anterior") return new Set(["13", "12", "11", "21", "22", "23"]);
        if (pos === "lower_anterior") return new Set(["43", "42", "41", "31", "32", "33"]);
        if (pos === "upper_canine") return new Set(["13", "23"]);
        return new Set();
    }, [plan.bite_ramps]);

    /* iter98.99cx — Treatment Tier row now shows the aligner range
       ("Tier 1 · 18–24 aligners"). Newer cases carry tier_aligner_min/
       tier_aligner_count in pricing (backend snapshot). Older cases
       fall back to a one-time /tiers/active fetch joined by tier_id.
       Hook MUST live before the early-exit below to satisfy Rules of
       Hooks (consistent call order across renders). */
    const [tiersIndex, setTiersIndex] = useState(null);
    const needsTierFetch = !!pricing.tier_id
        && (!pricing.tier_aligner_count || pricing.tier_aligner_count <= 0);
    useEffect(() => {
        if (!needsTierFetch) return;
        let cancelled = false;
        api.get("/tiers/active").then((r) => {
            if (cancelled) return;
            const list = Array.isArray(r.data) ? r.data : [];
            const idx = {};
            list.forEach((t) => { idx[t.id] = t; });
            setTiersIndex(idx);
        }).catch(() => { /* silent */ });
        return () => { cancelled = true; };
    }, [needsTierFetch]);

    /* Early exit — only for CoE cases where a tier has been committed */
    if (!inApproval || planning !== "coe" || !pricing.tier_id) return null;

    /* ── Derived values ──────────────────────────────────────────── */
    const eta = computeEta(plan);
    const weeks = Number(plan.estimated_duration_weeks) || 0;

    const upperAligners = Number(plan.aligner_count_upper) || 0;
    const lowerAligners = Number(plan.aligner_count_lower) || 0;
    /* iter98.99da — Stages = max(U, L) is the canonical clinical
       progression unit. Physical pieces = U + L is what manufacturing
       actually fabricates. We surface both: stages in Plan Summary
       headline, pieces as a small secondary detail. */
    const stagesCount = Math.max(upperAligners, lowerAligners);
    const totalAligners = upperAligners + lowerAligners;
    const cadence = plan.change_interval_days || plan.wear_schedule_default_days;
    const wearOverrides = plan.wear_schedule_overrides || [];

    const initialOJ = plan.initial_overjet_mm;
    const initialOB = plan.initial_overbite_mm;
    const finalOJ = plan.final_overjet_mm;
    const finalOB = plan.final_overbite_mm;
    const totalIpr = plan.total_ipr_mm;
    const hasBiteMetrics = (
        initialOJ != null || initialOB != null
        || finalOJ != null || finalOB != null
        || totalIpr != null
    );

    const hasProtocol = !!(plan.proposed_attachments || plan.proposed_ipr
        || plan.proposed_elastics || plan.planner_notes
        || (plan.has_overcorrection && plan.overcorrection_notes)
        || plan.aux_other_notes);

    const hasPerToothData = (
        movementsByTooth.size > 0 || attachmentsByTooth.size > 0
        || cutsByTooth.size > 0 || biteRampTeeth.size > 0
    );

    const biteRamps = plan.bite_ramps || {};

    /* iter98.99cr — Planner upfront flag: "this case may need a refinement
       round". Captured in PlanTimelineSection on the planner side
       (`plan.refinement_expected` + `refinement_expected_reason`) but
       was previously not surfaced to the doctor. Critical disclosure
       before approval — the doctor commits without knowing this. */
    const refinementExpected = !!plan.refinement_expected;
    const refinementReasonRaw = (plan.refinement_expected_reason || "").trim();
    const refinementReason = humaniseRefinementReason(refinementReasonRaw);

    /* iter98.99cs — Commercial summary rows shown inside Plan Summary
       (replaces the standalone "Payment & Offers" accordion which was
       redundant now that the approval dialog handles promo + payment
       terms end-to-end). The doctor sees what the planner committed
       commercially at a glance, alongside the clinical headline. */
    const labCharge = Number(pricing.lab_charge || 0);
    const priorityFee = Number(pricing.priority_fee || 0);
    const scannerRequested = !!pricing.scanner_pickup_requested;
    const scannerFee = Number(pricing.scanner_pickup_fee || 0);
    /* iter101.45 — Compliance Monitoring row. Doctors were seeing
       "Compliance Monitoring · Invisique manages it" on the case
       header (universal chip row) but the fee itself was hidden from
       the approval-time commercial breakdown. This surfaces the
       opt-in decision + the snapshotted fee (or "Included" when the
       admin has published a ₹0 fee). */
    const complianceOptedIn = c?.coe_support_compliance === true;
    const complianceFee = Number(pricing.invisique_managed_compliance_fee || 0);

    /* iter98.99cx — Treatment Tier row now shows the aligner range
       ("Tier 1 · 18–24 aligners"). The tiersIndex fetch hook lives
       BEFORE the early-return above; derived values are computed
       here so they're only paid for on renders where we actually
       render the hero. */
    const tierFromIndex = (tiersIndex && pricing.tier_id) ? tiersIndex[pricing.tier_id] : null;
    const tierMin = Number(pricing.tier_aligner_min ?? tierFromIndex?.aligner_min ?? 0);
    const tierMax = Number(pricing.tier_aligner_count ?? tierFromIndex?.aligner_count ?? 0);
    const tierRangeLabel = (() => {
        if (tierMax <= 0) return "";
        if (tierMin > 0 && tierMin !== tierMax) return `${tierMin}–${tierMax} stages`;
        return `Up to ${tierMax} stages`;
    })();
    const tierDisplay = pricing.tier_name
        ? (tierRangeLabel ? `${pricing.tier_name} · ${tierRangeLabel}` : pricing.tier_name)
        : "";

    return (
        <div className="space-y-3 mb-5" data-testid="plan-approval-hero">

            {/* ── REFINEMENT-EXPECTED CALLOUT (iter98.99cr) ──
                Planner flagged that a refinement round is likely. The
                doctor must see this before approving so they can either
                ask for revisions or proceed with eyes open. Amber
                advisory tone — not a blocker, just a heads-up. */}
            {refinementExpected && (
                <div
                    className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 via-amber-50/40 to-white p-4 sm:p-5 flex items-start gap-3 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
                    data-testid="approval-refinement-expected-callout"
                >
                    <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-800 grid place-items-center shrink-0 ring-1 ring-amber-200">
                        <ArrowsClockwise size={18} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-amber-800">
                            Planner advisory
                        </div>
                        <div className="mt-0.5 font-display text-[15px] font-bold text-amber-950 leading-tight">
                            Refinement round may be needed
                        </div>
                        {refinementReason && (
                            <div
                                className="mt-1.5 text-[13px] text-amber-900/90 leading-snug whitespace-pre-wrap"
                                data-testid="approval-refinement-expected-reason"
                            >
                                &ldquo;{refinementReason}&rdquo;
                            </div>
                        )}
                        <div className="mt-2 text-[11.5px] text-amber-800/80 leading-snug">
                            Your planner flagged this case as one that may benefit from a refinement round after the first set of aligners completes.
                            Approve only if you&apos;re comfortable with this expectation — otherwise request a revision to align on the timeline first.
                        </div>
                    </div>
                </div>
            )}

            {/* iter98.99ay — Tier headline card removed per user direction
                (2026-02-19). The tier name + Express TAT + cost / duration /
                ETA metrics were a duplicate of: tier name + speed row in
                the Payment & Offers panel (bottom of this hero), and
                duration / eta rows inside the Plan Summary accordion below.
                The "Jump to approval" CTA was also dropped — the page is
                short enough now that a manual scroll is fine. */}

            {/* ══════════════════════════════════════════════════════════
                1 ▸ PLAN SUMMARY  (open by default)
            ══════════════════════════════════════════════════════════ */}
            <ApprovalSection title="Plan Summary" icon={ListChecks} testId="approval-section-summary" forceOpen={documentMode}>
                <GlanceBox>
                    {/* iter98.99da — Stages-based clinical display.
                        "10 stages (10 U + 10 L = 20 physical aligners)"
                        Industry-standard: 1 stage = 1 upper + 1 lower
                        aligner set worn simultaneously. */}
                    <GlanceRow
                        k="Treatment stages"
                        v={
                            stagesCount > 0
                                ? `${stagesCount} stage${stagesCount === 1 ? "" : "s"} · ${upperAligners}U + ${lowerAligners}L = ${totalAligners} aligners total`
                                : "—"
                        }
                        testId="approval-glance-aligners"
                    />
                    <GlanceRow k="Wear schedule" v={cadence ? `${cadence} days per stage` : "—"} testId="approval-glance-cadence" />
                    <GlanceRow k="Estimated duration" v={weeks ? `${weeks} weeks (${weeks * 7} days)` : "—"} testId="approval-glance-duration" />
                    <GlanceRow k="Est. completion date" v={eta || "—"} testId="approval-glance-eta" />
                    {plan.retainer_type && (
                        <GlanceRow
                            k="Retainer type"
                            v={humanize(plan.retainer_type)}
                            testId="approval-glance-retainer"
                        />
                    )}
                    {(plan.retention_recall_first_months || plan.retention_recall_months) && (
                        <GlanceRow
                            k="Recall schedule"
                            v={`${plan.retention_recall_first_months || plan.retention_recall_months} months`}
                            testId="approval-glance-recall"
                        />
                    )}

                    {/* iter98.99cs — Commercial commitments rolled into
                        Plan Summary so the doctor sees tier + lab + speed +
                        IO-scan at the same glance as the clinical metrics.
                        Promo/total is deferred to the approval dialog. */}
                    {/* iter98.99ct — Visual section break so clinical and
                        commercial rows are unambiguously distinct. Renders
                        only when at least one commercial row has data. */}
                    {(pricing.tier_name || labCharge > 0 || pricing.priority_label || scannerRequested || complianceOptedIn) && (
                        <div className="flex items-center gap-2 pt-2 pb-0.5" data-testid="approval-glance-commercial-divider">
                            <div className="h-px flex-1 bg-border/60" />
                            <span className="text-[9.5px] font-bold tracking-[0.18em] uppercase text-muted-foreground/80">
                                Commercial
                            </span>
                            <div className="h-px flex-1 bg-border/60" />
                        </div>
                    )}
                    {pricing.tier_name && (
                        <GlanceRow
                            k="Treatment Tier"
                            v={tierDisplay}
                            testId="approval-glance-tier"
                        />
                    )}
                    {labCharge > 0 && (
                        <GlanceRow
                            k="Lab charges"
                            v={fmtINR(labCharge)}
                            testId="approval-glance-lab-charge"
                        />
                    )}
                    {pricing.priority_label && (
                        <GlanceRow
                            k="Manufacturing speed"
                            v={
                                priorityFee > 0
                                    ? `${pricing.priority_label} (+ ${fmtINR(priorityFee)})`
                                    : `${pricing.priority_label} (Included)`
                            }
                            testId="approval-glance-priority"
                        />
                    )}
                    {scannerRequested && (
                        <GlanceRow
                            k="IO Scans by Invisique™"
                            v={
                                scannerFee > 0
                                    ? `Opted in (+ ${fmtINR(scannerFee)})`
                                    : "Opted in (Included)"
                            }
                            testId="approval-glance-scanner"
                        />
                    )}
                    {complianceOptedIn && (
                        <GlanceRow
                            k="Compliance Monitoring & Support"
                            v={
                                complianceFee > 0
                                    ? `Invisique™ manages it (+ ${fmtINR(complianceFee)})`
                                    : "Invisique™ manages it (Included)"
                            }
                            testId="approval-glance-compliance"
                        />
                    )}
                </GlanceBox>
            </ApprovalSection>

            {/* ══════════════════════════════════════════════════════════
                3 ▸ BITE METRICS
            ══════════════════════════════════════════════════════════ */}
            {hasBiteMetrics && (
                <ApprovalSection title="Bite Metrics" icon={ChartLine} testId="approval-section-bite-metrics" defaultOpen={false} forceOpen={documentMode}>
                    <div>
                        <FieldLabel>Initial measurements</FieldLabel>
                        <div className="grid grid-cols-2 gap-3">
                            <KVField label="Overbite (OB)" value={initialOB != null ? `${initialOB} mm` : "—"} testId="approval-bite-initial-ob" />
                            <KVField label="Overjet (OJ)" value={initialOJ != null ? `${initialOJ} mm` : "—"} testId="approval-bite-initial-oj" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <FieldLabel>Final target measurements</FieldLabel>
                        <div className="grid grid-cols-2 gap-3">
                            <KVField label="Target overbite" value={finalOB != null ? `${finalOB} mm` : "—"} testId="approval-bite-final-ob" />
                            <KVField label="Target overjet" value={finalOJ != null ? `${finalOJ} mm` : "—"} testId="approval-bite-final-oj" />
                        </div>
                    </div>
                    <div className="mt-4">
                        <FieldLabel>Outcome shift</FieldLabel>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <DeltaCard
                                label="Overbite"
                                before={initialOB}
                                after={finalOB}
                                unit="mm"
                                testId="approval-bite-delta-ob"
                            />
                            <DeltaCard
                                label="Overjet"
                                before={initialOJ}
                                after={finalOJ}
                                unit="mm"
                                testId="approval-bite-delta-oj"
                            />
                        </div>
                    </div>
                    {totalIpr != null && (
                        <div className="mt-4">
                            <FieldLabel>Total IPR planned</FieldLabel>
                            <div className="rounded-md border border-border bg-secondary px-3 py-2 inline-flex items-center gap-2 text-sm font-semibold text-amber-700" data-testid="approval-bite-total-ipr">
                                <ArrowsHorizontal size={14} weight="fill" />
                                {totalIpr} mm
                            </div>
                        </div>
                    )}
                </ApprovalSection>
            )}

            {/* ══════════════════════════════════════════════════════════
                4 ▸ CLINICAL PROTOCOL
            ══════════════════════════════════════════════════════════ */}
            {hasProtocol && (
                <ApprovalSection title="Clinical Protocol" icon={ClipboardText} testId="approval-section-protocol" defaultOpen={false} forceOpen={documentMode}>
                    {plan.proposed_attachments && (
                        <ProtocolField label="Attachments — location & stage" testId="approval-protocol-attachments">
                            {plan.proposed_attachments}
                        </ProtocolField>
                    )}
                    {plan.proposed_ipr && (
                        <ProtocolField label="IPR by region & stage" testId="approval-protocol-ipr">
                            {plan.proposed_ipr}
                        </ProtocolField>
                    )}
                    {plan.proposed_elastics && (
                        <ProtocolField label="Elastics protocol" testId="approval-protocol-elastics">
                            {plan.proposed_elastics}
                        </ProtocolField>
                    )}
                    {plan.planner_notes && (
                        <ProtocolField
                            label="Planner notes & rationale"
                            testId="approval-protocol-planner-notes"
                            icon={Quotes}
                            accent
                        >
                            {plan.planner_notes}
                        </ProtocolField>
                    )}
                    {plan.has_overcorrection && plan.overcorrection_notes && (
                        <ProtocolField label="Overcorrection details" testId="approval-protocol-overcorrection">
                            {plan.overcorrection_notes}
                        </ProtocolField>
                    )}
                    {plan.aux_other_notes && (
                        <ProtocolField label="Other auxiliaries & staging notes" testId="approval-protocol-other-aux">
                            {plan.aux_other_notes}
                        </ProtocolField>
                    )}
                </ApprovalSection>
            )}

            {/* ══════════════════════════════════════════════════════════
                5 ▸ PER-TOOTH PLAN — tabbed
            ══════════════════════════════════════════════════════════ */}
            {hasPerToothData && (
                <ApprovalSection title="Per-Tooth Plan — Authored Movements" icon={Compass} testId="approval-section-per-tooth" defaultOpen={false} forceOpen={documentMode}>
                    <PerToothTabs
                        movements={movementsByTooth}
                        attachments={attachmentsByTooth}
                        cuts={cutsByTooth}
                        biteRampTeeth={biteRampTeeth}
                        biteRamps={biteRamps}
                    />
                </ApprovalSection>
            )}

            {/* ══════════════════════════════════════════════════════════
                6 ▸ ALIGNERS & WEAR SCHEDULE
            ══════════════════════════════════════════════════════════ */}
            <ApprovalSection title="Aligners & Wear Schedule" icon={Pill} testId="approval-section-wear-schedule" defaultOpen={false} forceOpen={documentMode}>
                <FieldLabel>Wear schedule</FieldLabel>
                <FieldValue testId="approval-wear-schedule-summary">
                    <Line><strong>Standard:</strong> {cadence ? `${cadence} days per stage` : "—"}</Line>
                    <Line><strong>Upper aligners:</strong> {upperAligners || "—"}</Line>
                    <Line><strong>Lower aligners:</strong> {lowerAligners || "—"}</Line>
                    {totalAligners > 0 && (
                        <Line><strong>Total stages:</strong> {totalAligners}</Line>
                    )}
                    {weeks > 0 && (
                        <Line><strong>Total treatment duration:</strong> {weeks * 7} days ({weeks} weeks)</Line>
                    )}
                </FieldValue>

                {wearOverrides.length > 0 && (
                    <>
                        <FieldLabel>Stage-specific overrides</FieldLabel>
                        <FieldValue testId="approval-wear-schedule-overrides">
                            {wearOverrides.map((ov, i) => (
                                <Line key={`${ov.stage}-${i}`}>
                                    Stage {ov.stage}: {ov.days} days {ov.note ? <span className="text-muted-foreground">— {ov.note}</span> : null}
                                </Line>
                            ))}
                        </FieldValue>
                    </>
                )}
            </ApprovalSection>

            {/* iter98.99be — Removed the standalone "Bite Ramps — Summary"
                accordion (2026-02-19). It duplicated the Bite Ramps tab
                inside the Per-Tooth Plan section above, which already
                renders the placement chart, configuration, height,
                stage onset and clinical notes. Keeping both surfaces the
                same data twice on the Treatment Plan tab. */}

            {/* ══════════════════════════════════════════════════════════
                8 ▸ RETENTION PLAN
            ══════════════════════════════════════════════════════════ */}
            {(plan.retainer_type || plan.retention_schedule
                || plan.retention_recall_first_months || plan.retention_recall_second_months
                || plan.retention_recall_months) && (
                <ApprovalSection title="Retention Plan" icon={Anchor} testId="approval-section-retention" defaultOpen={false} forceOpen={documentMode}>
                    {plan.retainer_type && (
                        <>
                            <FieldLabel>Retainer type</FieldLabel>
                            <FieldValue testId="approval-retention-type">
                                {humanize(plan.retainer_type)}
                                {plan.retainer_type === "other" && plan.retainer_other_text
                                    ? ` — ${plan.retainer_other_text}`
                                    : ""}
                            </FieldValue>
                        </>
                    )}
                    {plan.retention_schedule && (
                        <>
                            <FieldLabel>Retainer wear protocol</FieldLabel>
                            <FieldValue testId="approval-retention-schedule">{plan.retention_schedule}</FieldValue>
                        </>
                    )}
                    {(plan.retention_recall_first_months || plan.retention_recall_months) && (
                        <>
                            <FieldLabel>First recall</FieldLabel>
                            <FieldValue testId="approval-retention-recall-1">
                                {plan.retention_recall_first_months || plan.retention_recall_months} months after treatment completion
                            </FieldValue>
                        </>
                    )}
                    {plan.retention_recall_second_months && (
                        <>
                            <FieldLabel>Second recall</FieldLabel>
                            <FieldValue testId="approval-retention-recall-2">
                                {plan.retention_recall_second_months} months after treatment completion
                            </FieldValue>
                        </>
                    )}
                </ApprovalSection>
            )}

            {/* ══════════════════════════════════════════════════════════
                INTERACTIVE 3D PLAN + LEGACY PLAN VIEWER  (side-by-side)
                iter98.99az — Per user direction (2026-02-19): Request
                changes button removed (it duplicates the iteration card
                decision footer below where revisions are formally
                requested as part of the approval flow).
                iter98.99bc — Added a side-by-side "View entire plan"
                card that opens the formal treatment-plan document in a
                NEW WINDOW (was a Dialog in 98.99bb). Document mode
                hides both these cards since the document IS the long
                view.
                iter98.99dd — Empty "3D viewer not attached" placeholder
                tile dropped. When the planner hasn't attached an
                external 3D link, we just show "View entire treatment
                plan" full-width — no dead chrome.
            ══════════════════════════════════════════════════════════ */}
            {!documentMode && (
                <div className={plan.external_link ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : ""}>
                    {plan.external_link && (
                        <a
                            href={plan.external_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group block rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.06] to-primary/[0.02] hover:from-primary/[0.10] hover:to-primary/[0.05] hover:border-primary/60 transition-all px-4 sm:px-5 py-4"
                            data-testid="approval-hero-deliverable-link"
                        >
                            <div className="flex items-center gap-3.5">
                                <div className="shrink-0 w-11 h-11 rounded-xl bg-primary/10 grid place-items-center ring-1 ring-primary/30 group-hover:bg-primary group-hover:ring-primary transition-colors">
                                    <ArrowUpRight size={20} weight="bold" className="text-primary group-hover:text-white transition-colors" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display text-sm sm:text-[15px] font-bold text-foreground leading-tight">
                                        Open the interactive 3D plan
                                    </div>
                                    <p className="text-[12px] text-muted-foreground leading-snug mt-0.5">
                                        Stage-by-stage in your planner&apos;s 3D viewer{plan.planning_software ? ` (${plan.planning_software})` : ""}.
                                    </p>
                                </div>
                                <span className="hidden sm:inline-flex shrink-0 items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-primary group-hover:translate-x-0.5 transition-transform">
                                    Open <ArrowUpRight size={11} weight="bold" />
                                </span>
                            </div>
                        </a>
                    )}

                    {/* iter98.99bc — Open the formal treatment-plan document
                        in a new browser tab. The doc page has letterhead,
                        patient identity, meta strip, full plan body, and
                        sign-off — it's the printable "treatment plan from
                        Invisique to the doctor". */}
                    <a
                        href={`/treatment-plan/${c.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group block rounded-2xl border border-border bg-card hover:border-primary/40 hover:bg-secondary/30 transition-all px-4 sm:px-5 py-4"
                        data-testid="approval-hero-view-legacy-plan"
                    >
                        <div className="flex items-center gap-3.5">
                            <div className="shrink-0 w-11 h-11 rounded-xl bg-secondary grid place-items-center ring-1 ring-border group-hover:bg-primary/10 group-hover:ring-primary/30 transition-colors">
                                <FileText size={20} weight="duotone" className="text-foreground/70 group-hover:text-primary transition-colors" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-display text-sm sm:text-[15px] font-bold text-foreground leading-tight">
                                    View entire treatment plan
                                </div>
                                <p className="text-[12px] text-muted-foreground leading-snug mt-0.5">
                                    Open the full Invisique™ treatment-plan document in a new tab — printable, complete, professional.
                                </p>
                            </div>
                            <span className="hidden sm:inline-flex shrink-0 items-center gap-1 text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all">
                                Open <ArrowUpRight size={11} weight="bold" />
                            </span>
                        </div>
                    </a>
                </div>
            )}

            {/* iter98.99cs — "Payment & Offers" accordion removed per
                user direction. Tier name, lab charges, manufacturing
                speed, and IO scanning service are now surfaced inside
                Plan Summary (top accordion). Promo selection, payment
                terms (pay later / advance / in full), and final total
                live exclusively in the Finalize & Approve dialog so the
                doctor commits to commercial terms at one decisive
                moment instead of two scattered surfaces. */}
        </div>
    );
}


/* ────────────────── ApprovalSection (collapsible) ────────────────── */

export function ApprovalSection({ title, icon: Icon, children, testId, defaultOpen = false, forceOpen = false }) {
    const [openState, setOpen] = useState(defaultOpen);
    const open = forceOpen ? true : openState;
    return (
        <div
            className={`rounded-2xl border border-border bg-card overflow-hidden ${open ? "shadow-sm" : ""}`}
            data-testid={testId}
        >
            {forceOpen ? (
                <div className="bg-secondary/60 px-4 py-3 border-b border-border/70 flex items-center gap-3">
                    <span className="shrink-0 w-[3px] h-5 rounded-sm bg-primary" />
                    {Icon && <Icon size={15} weight="duotone" className="text-primary shrink-0" />}
                    <span className="font-display text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                        {title}
                    </span>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="w-full bg-secondary/60 hover:bg-secondary px-4 py-3 border-b border-border/70 flex items-center gap-3 transition-colors text-left"
                    aria-expanded={open}
                    data-testid={`${testId}-toggle`}
                >
                    {/* Theme-colored bar accent on the toggle row (NOT the full
                        container — keeps with the user's mockup which has a
                        3px green bar on the section header). */}
                    <span className="shrink-0 w-[3px] h-5 rounded-sm bg-primary" />
                    {Icon && <Icon size={15} weight="duotone" className="text-primary shrink-0" />}
                    <span className="font-display text-sm font-semibold text-foreground flex-1 min-w-0 truncate">
                        {title}
                    </span>
                    <CaretDown
                        size={14}
                        weight="bold"
                        className={`text-muted-foreground transition-transform shrink-0 ${open ? "" : "-rotate-90"}`}
                    />
                </button>
            )}
            {open && (
                <div className="p-4 sm:p-5" data-testid={`${testId}-content`}>
                    {children}
                </div>
            )}
        </div>
    );
}


/* ────────────────── Atoms ────────────────── */

function GlanceBox({ children }) {
    return (
        <div className="rounded-md border border-border bg-secondary/40 px-4 py-2">
            {children}
        </div>
    );
}

function GlanceRow({ k, v, testId }) {
    return (
        <div className="flex items-center justify-between gap-3 py-1.5 border-b border-border/40 last:border-b-0 text-[13px]" data-testid={testId}>
            <span className="text-[12px] text-muted-foreground">{k}</span>
            <span className="font-semibold text-foreground tabular-nums">{v}</span>
        </div>
    );
}

function FieldLabel({ children }) {
    return (
        <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-muted-foreground mt-3 first:mt-0 mb-1.5">
            {children}
        </div>
    );
}

function FieldValue({ children, testId }) {
    return (
        <div
            className="text-[13px] text-foreground rounded-md border border-border bg-secondary/40 px-3 py-2.5 leading-relaxed mb-2 whitespace-pre-line"
            data-testid={testId}
        >
            {children}
        </div>
    );
}

function KVField({ label, value, testId }) {
    return (
        <div data-testid={testId}>
            <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5">{label}</div>
            <div className="text-[13px] font-semibold rounded-md border border-border bg-secondary/40 px-3 py-2.5">{value}</div>
        </div>
    );
}

function Line({ children }) {
    return <div className="leading-relaxed">{children}</div>;
}

function ProtocolField({ label, children, accent = false, icon: Icon = null, testId }) {
    return (
        <div className="mb-3 last:mb-0">
            <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1.5 inline-flex items-center gap-1.5">
                {Icon && <Icon size={11} weight="fill" className={accent ? "text-primary" : ""} />}
                {label}
            </div>
            <div
                className={`text-[13px] leading-relaxed whitespace-pre-line rounded-md border px-3 py-2.5 ${accent ? "bg-primary/[0.05] border-primary/20 text-foreground" : "bg-secondary/40 border-border text-foreground"}`}
                data-testid={testId}
            >
                {children}
            </div>
        </div>
    );
}

function DeltaCard({ label, before, after, unit, testId }) {
    if (before == null && after == null) return null;
    const b = before != null ? Number(before) : null;
    const a = after != null ? Number(after) : null;
    const delta = (b != null && a != null) ? (a - b) : null;
    const tone = deltaTone(delta);
    const deltaText = delta != null ? `${delta > 0 ? "+" : ""}${delta.toFixed(1)}${unit}` : null;
    const pill = {
        improving: "bg-primary/10 text-primary border-primary/30",
        same: "bg-secondary text-foreground/70 border-border",
        worsening: "bg-amber-50 text-amber-800 border-amber-200",
        neutral: "bg-secondary text-foreground/70 border-border",
    }[tone];
    return (
        <div className="rounded-md border border-border bg-secondary/40 px-3 py-2.5" data-testid={testId}>
            <div className="text-[10.5px] font-bold tracking-[0.12em] uppercase text-muted-foreground mb-1">{label}</div>
            <div className="flex items-center gap-2">
                <span className="font-display text-base font-semibold tabular-nums text-muted-foreground">
                    {b != null ? `${b}${unit}` : "—"}
                </span>
                <ArrowRight size={13} className="text-primary shrink-0" weight="bold" />
                <span className="font-display text-base font-bold tabular-nums text-foreground">
                    {a != null ? `${a}${unit}` : "—"}
                </span>
            </div>
            {deltaText && (
                <div className={`mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10.5px] font-bold tabular-nums ${pill}`} data-testid={`${testId}-delta`}>
                    {tone === "improving" && <ArrowDown size={9} weight="bold" />}
                    {deltaText}
                </div>
            )}
        </div>
    );
}


/* ────────────────── Per-Tooth Plan tabs ────────────────── */

function PerToothTabs({ movements, attachments, cuts, biteRampTeeth, biteRamps }) {
    /* Default to the tab that has data — preferred order: movements →
       attachments → cuts → biteramps. Guarantees the doctor never lands
       on a blank tab when only one feature is authored. */
    const initial = useMemo(() => {
        if (movements.size > 0) return "movements";
        if (attachments.size > 0) return "attachments";
        if (cuts.size > 0) return "cuts";
        return "biteramps";
    }, [movements.size, attachments.size, cuts.size]);
    const [tab, setTab] = useState(initial);
    useEffect(() => { setTab(initial); }, [initial]);

    /* iter98.99ax — Patient-mode toggle. When ON, every tooth tile
       label switches from FDI ("13") to a 3-char anatomical shorthand
       ("UR3") and authored-list headings include the full anatomy
       ("Upper right canine"). Use chair-side to walk the patient
       through the plan. */
    const [labelMode, setLabelMode] = useState("fdi");

    const tabs = [
        { key: "movements",   count: movements.size,    available: movements.size > 0 },
        { key: "attachments", count: attachments.size,  available: attachments.size > 0 },
        { key: "cuts",        count: cuts.size,         available: cuts.size > 0 },
        { key: "biteramps",   count: biteRampTeeth.size, available: biteRampTeeth.size > 0 },
    ];

    return (
        <div data-testid="approval-per-tooth-tabs">
            {/* Tab nav + Patient-mode toggle */}
            <div className="flex items-center gap-2 border-b border-border/70 mb-4 overflow-x-auto -mx-1 px-1">
                <div className="flex gap-2 flex-1 min-w-0">
                    {tabs.map((t) => {
                        const theme = TAB_THEMES[t.key];
                        const active = tab === t.key;
                        const Icon = theme.icon;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => setTab(t.key)}
                                disabled={!t.available}
                                className={`relative px-3 py-2 text-[12.5px] font-semibold inline-flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${
                                    active
                                        ? `border-current ${theme.text}`
                                        : "border-transparent text-muted-foreground hover:text-foreground"
                                } ${!t.available ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                data-testid={`approval-per-tooth-tab-${t.key}`}
                                aria-selected={active}
                            >
                                <Icon size={13} weight={active ? "fill" : "duotone"} />
                                {theme.label}
                                {t.count > 0 && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold tabular-nums ${active ? `${theme.tint} ${theme.text}` : "bg-secondary text-muted-foreground"}`}>
                                        {t.count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Patient-mode toggle — segmented 2-button pill */}
                <div
                    className="shrink-0 inline-flex items-center bg-secondary rounded-full p-0.5 ring-1 ring-border"
                    role="radiogroup"
                    aria-label="Tooth label mode"
                    data-testid="approval-per-tooth-label-mode"
                    title="Switch how teeth are labelled — useful for explaining the plan to the patient chair-side."
                >
                    <button
                        type="button"
                        onClick={() => setLabelMode("fdi")}
                        className={`px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-wide uppercase transition-colors ${labelMode === "fdi" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        aria-pressed={labelMode === "fdi"}
                        data-testid="approval-per-tooth-mode-fdi"
                    >
                        FDI
                    </button>
                    <button
                        type="button"
                        onClick={() => setLabelMode("patient")}
                        className={`px-2.5 py-1 rounded-full text-[10.5px] font-bold tracking-wide uppercase transition-colors inline-flex items-center gap-1 ${labelMode === "patient" ? "bg-card text-primary shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                        aria-pressed={labelMode === "patient"}
                        data-testid="approval-per-tooth-mode-patient"
                    >
                        <ToothIcon size={11} weight="fill" />
                        Patient view
                    </button>
                </div>
            </div>

            {/* Tab body */}
            {tab === "movements"   && <PerToothMovementsTab   data={movements}     labelMode={labelMode} />}
            {tab === "attachments" && <PerToothAttachmentsTab data={attachments}   labelMode={labelMode} />}
            {tab === "cuts"        && <PerToothCutsTab        data={cuts}          labelMode={labelMode} />}
            {tab === "biteramps"   && <PerToothBiteRampsTab   teeth={biteRampTeeth} biteRamps={biteRamps} labelMode={labelMode} />}
        </div>
    );
}


/* ── Generic FDI chart with coloured dots ── */
function ToothChartGrid({ activeTeeth, theme, testId, labelMode = "fdi", compact = false }) {
    /* `activeTeeth` is a Set<string> of FDI tooth numbers that should
       render the colored dot. `compact` shrinks tooth tiles + drops the
       outer "Upper R / Upper L" labels so the chart fits in a half
       column without horizontal scroll. */
    return (
        <div className={`rounded-md bg-secondary/40 border border-border ${compact ? "p-1.5 space-y-1.5" : "p-3 space-y-3"}`} data-testid={testId}>
            <ArchRow
                leftLabel="Upper R"
                rightLabel="Upper L"
                rightQuadrant={UPPER_RIGHT}
                leftQuadrant={UPPER_LEFT}
                activeTeeth={activeTeeth}
                theme={theme}
                labelMode={labelMode}
                compact={compact}
            />
            <ArchRow
                leftLabel="Lower R"
                rightLabel="Lower L"
                rightQuadrant={LOWER_RIGHT}
                leftQuadrant={LOWER_LEFT}
                activeTeeth={activeTeeth}
                theme={theme}
                labelMode={labelMode}
                compact={compact}
            />
        </div>
    );
}

function ArchRow({ leftLabel, rightLabel, rightQuadrant, leftQuadrant, activeTeeth, theme, labelMode, compact }) {
    if (compact) {
        /* Compact: drop side labels, tiny gaps, tooth tiles shrunk via
           Tooth's own `compact` prop. Designed to fit ~360px column. */
        return (
            <div className="flex items-center justify-center gap-0.5">
                <div className="flex gap-0.5 shrink-0">
                    {rightQuadrant.map((t) => (
                        <Tooth key={t} fdi={t} active={activeTeeth.has(t)} theme={theme} labelMode={labelMode} compact />
                    ))}
                </div>
                <div className="w-px h-5 bg-border mx-1 shrink-0" />
                <div className="flex gap-0.5 shrink-0">
                    {leftQuadrant.map((t) => (
                        <Tooth key={t} fdi={t} active={activeTeeth.has(t)} theme={theme} labelMode={labelMode} compact />
                    ))}
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-center justify-center gap-1.5 overflow-x-auto">
            <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground w-14 text-center shrink-0">{leftLabel}</span>
            <div className="flex gap-1 shrink-0">
                {rightQuadrant.map((t) => (
                    <Tooth key={t} fdi={t} active={activeTeeth.has(t)} theme={theme} labelMode={labelMode} />
                ))}
            </div>
            {/* Midline divider */}
            <div className="w-px h-7 bg-border mx-1 shrink-0" />
            <div className="flex gap-1 shrink-0">
                {leftQuadrant.map((t) => (
                    <Tooth key={t} fdi={t} active={activeTeeth.has(t)} theme={theme} labelMode={labelMode} />
                ))}
            </div>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground w-14 text-center shrink-0">{rightLabel}</span>
        </div>
    );
}

function Tooth({ fdi, active, theme, labelMode = "fdi", compact = false }) {
    const label = toothTileLabel(fdi, labelMode);
    const isPatient = labelMode === "patient";
    const sizeCls = compact
        ? (isPatient ? "w-7 h-6 text-[8px]" : "w-5 h-6 text-[8px]")
        : (isPatient ? "w-9 h-8 text-[9.5px]" : "w-7 h-8 text-[9.5px]");
    return (
        <div
            className={`relative ${sizeCls} rounded border font-semibold grid place-items-center transition-all ${
                active
                    ? `${theme.tint} ${theme.text} border-current/30 ring-1 ${theme.ring}`
                    : "bg-card border-border text-muted-foreground"
            }`}
            data-testid={`approval-per-tooth-${fdi}${active ? "-active" : ""}`}
            title={toothTitle(fdi, labelMode, active)}
        >
            {label}
            {active && (
                <span className={`absolute bottom-0.5 right-0.5 ${compact ? "w-1 h-1" : "w-1.5 h-1.5"} rounded-full ${theme.dot}`} />
            )}
        </div>
    );
}

function AuthoredList({ items, theme, emptyText, renderItem, testId }) {
    if (items.length === 0) {
        return (
            <div className="text-[12.5px] text-muted-foreground italic mt-3" data-testid={`${testId}-empty`}>
                {emptyText}
            </div>
        );
    }
    return (
        <div className={`mt-4 rounded-md border ${theme.ring.replace("ring-", "border-")} ${theme.tint} p-3 space-y-2`} data-testid={testId}>
            <div className={`text-[10.5px] font-bold tracking-[0.18em] uppercase ${theme.text} mb-1`}>
                Authored summary ({items.length})
            </div>
            {items.map((it, i) => (
                <div key={`${it.tooth}-${i}`} className="rounded bg-card border border-border/40 px-3 py-2" data-testid={`${testId}-row-${it.tooth}`}>
                    {renderItem(it)}
                </div>
            ))}
        </div>
    );
}


/* ── Movements tab ── */
function PerToothMovementsTab({ data, labelMode, compact = false }) {
    const theme = TAB_THEMES.movements;
    const teeth = new Set(data.keys());
    const items = Array.from(data.values());
    return (
        <>
            <FieldLabel>Tooth movements chart</FieldLabel>
            <ToothChartGrid activeTeeth={teeth} theme={theme} testId="approval-per-tooth-chart-movements" labelMode={labelMode} compact={compact} />
            <AuthoredList
                items={items}
                theme={theme}
                emptyText="No per-tooth movements authored."
                testId="approval-authored-movements"
                renderItem={(it) => {
                    const dirs = (it.directions || []).map(humanize).join(", ");
                    const mt = it.movement_type ? humanize(it.movement_type) : null;
                    const mag = it.magnitude != null ? `${it.magnitude}${["rotation", "torque", "tip"].includes(it.movement_type) ? "°" : "mm"}` : null;
                    const range = (it.start_stage != null || it.end_stage != null)
                        ? `Stages ${it.start_stage || "?"}–${it.end_stage || "?"}` : null;
                    return (
                        <>
                            <div className={`font-bold text-[12.5px] ${theme.text} mb-0.5`}>{toothHeading(it.tooth, labelMode)}</div>
                            <div className="text-[11.5px] text-foreground/85 leading-relaxed">
                                {mt && <span className="font-semibold">{mt}</span>}
                                {mt && (mag || dirs) ? " · " : ""}
                                {mag && <span className="tabular-nums">{mag}</span>}
                                {mag && dirs ? " · " : ""}
                                {dirs && <span>{dirs}</span>}
                                {it.amount_mm != null && <span> · {it.amount_mm} mm linear</span>}
                                {it.rotation_deg != null && <span> · {it.rotation_deg}° rotation</span>}
                                {it.torque_deg != null && <span> · {it.torque_deg}° torque</span>}
                                {it.anchorage && <span className="text-muted-foreground"> · anchorage: {humanize(it.anchorage)}</span>}
                                {range && <span className="text-muted-foreground"> · {range}</span>}
                            </div>
                            {it.notes && (
                                <div className="text-[11px] text-muted-foreground mt-1">{it.notes}</div>
                            )}
                        </>
                    );
                }}
            />
        </>
    );
}


/* ── Attachments tab ── */
function PerToothAttachmentsTab({ data, labelMode, compact = false }) {
    const theme = TAB_THEMES.attachments;
    const teeth = new Set(data.keys());
    const items = Array.from(data.values());
    return (
        <>
            <FieldLabel>Attachment placement chart</FieldLabel>
            <ToothChartGrid activeTeeth={teeth} theme={theme} testId="approval-per-tooth-chart-attachments" labelMode={labelMode} compact={compact} />
            <AuthoredList
                items={items}
                theme={theme}
                emptyText="No per-tooth attachments authored."
                testId="approval-authored-attachments"
                renderItem={(it) => {
                    const parts = [];
                    if (it.shape) parts.push(humanize(it.shape));
                    if (it.size) parts.push(humanize(it.size));
                    return (
                        <>
                            <div className={`font-bold text-[12.5px] ${theme.text} mb-0.5`}>{toothHeading(it.tooth, labelMode)}</div>
                            <div className="text-[11.5px] text-foreground/85 leading-relaxed">
                                {parts.length > 0 && <span>{parts.join(" · ")}</span>}
                                {it.purpose && <span className="text-muted-foreground">{parts.length > 0 ? " · " : ""}{humanize(it.purpose)}</span>}
                                {it.stage_onset != null && <span> · stage {it.stage_onset}</span>}
                            </div>
                            {it.notes && (
                                <div className="text-[11px] text-muted-foreground mt-1">{it.notes}</div>
                            )}
                        </>
                    );
                }}
            />
        </>
    );
}


/* ── Precision cuts tab ── */
function PerToothCutsTab({ data, labelMode, compact = false }) {
    const theme = TAB_THEMES.cuts;
    const teeth = new Set(data.keys());
    const items = Array.from(data.values());
    return (
        <>
            <FieldLabel>Precision cuts placement chart</FieldLabel>
            <ToothChartGrid activeTeeth={teeth} theme={theme} testId="approval-per-tooth-chart-cuts" labelMode={labelMode} compact={compact} />
            <AuthoredList
                items={items}
                theme={theme}
                emptyText="No per-tooth precision cuts authored."
                testId="approval-authored-cuts"
                renderItem={(it) => (
                    <>
                        <div className={`font-bold text-[12.5px] ${theme.text} mb-0.5`}>{toothHeading(it.tooth, labelMode)}</div>
                        <div className="text-[11.5px] text-foreground/85 leading-relaxed">
                            {it.purpose && <span className="font-semibold">{humanize(it.purpose)}</span>}
                            {it.position && <span className="text-muted-foreground"> · {humanize(it.position)}</span>}
                            {it.depth_mm != null && <span> · {it.depth_mm} mm depth</span>}
                            {it.stage_onset != null && <span> · from stage {it.stage_onset}</span>}
                        </div>
                        {it.notes && (
                            <div className="text-[11px] text-muted-foreground mt-1">{it.notes}</div>
                        )}
                    </>
                )}
            />
        </>
    );
}


/* ── Bite ramps tab ── */
function PerToothBiteRampsTab({ teeth, biteRamps, labelMode, compact = false }) {
    const theme = TAB_THEMES.biteramps;
    const teethList = Array.from(teeth).sort();
    /* Patient-mode: show full anatomical names list ("Upper right canine, …") */
    const teethDisplay = labelMode === "patient"
        ? teethList.map((t) => `${fdiToAnatomy(t)} (${t})`).join(", ")
        : teethList.join(", ");
    return (
        <>
            <FieldLabel>Bite ramp placement chart</FieldLabel>
            <ToothChartGrid activeTeeth={teeth} theme={theme} testId="approval-per-tooth-chart-biteramps" labelMode={labelMode} compact={compact} />
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="approval-authored-biteramps">
                <div className="text-[10.5px] font-bold tracking-[0.18em] uppercase text-amber-800 mb-1">
                    Bite ramp configuration
                </div>
                <div className="rounded bg-card border border-border/40 px-3 py-2 text-[12px] leading-relaxed">
                    {biteRamps.position && <Line><strong>Placement:</strong> {humanize(biteRamps.position)}</Line>}
                    {teeth.size > 0 && <Line><strong>Teeth:</strong> {teethDisplay}</Line>}
                    {biteRamps.ramp_height_mm != null && <Line><strong>Height:</strong> {biteRamps.ramp_height_mm} mm</Line>}
                    {biteRamps.stage_onset != null && <Line><strong>Active from stage:</strong> {biteRamps.stage_onset}</Line>}
                    {biteRamps.notes && (
                        <div className="mt-2 text-muted-foreground"><strong>Notes:</strong> {biteRamps.notes}</div>
                    )}
                    {!biteRamps.position && !biteRamps.ramp_height_mm && !biteRamps.stage_onset && !biteRamps.notes && (
                        <span className="text-muted-foreground italic">Bite ramps flagged on the plan — no detail authored beyond the toggle.</span>
                    )}
                </div>
            </div>
        </>
    );
}


/* iter98.99cs — ApprovalPricingPanel (Payment & Offers accordion) +
   the Row helper that supported it have been removed. Commercial
   summary now lives inside Plan Summary (Treatment Tier · Lab charges
   · Manufacturing speed · IO Scanning Service). Promo selection,
   payment terms, and final total are owned exclusively by the
   Finalize & Approve dialog so the doctor commits to commercials at
   one decisive moment. */


/* ──────────────────────────────────────────────────────────────────
   Public exports for reuse outside the hero (e.g. RequestRevisionPage
   needs to embed the Per-Tooth Plan tabs on the planner-read-only
   side so the section matches the Treatment-Plan tab one-to-one).
   ────────────────────────────────────────────────────────────────── */

/* Compute the per-tooth Maps/Set the same way the hero does internally,
   then render the 4-tab Per-Tooth Plan UI. Pass just the raw `plan`
   object; everything else is derived. */
export function PerToothPlanView({ plan }) {
    const movements = useMemo(() => {
        const m = new Map();
        (plan?.tooth_movements_plan || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan?.tooth_movements_plan]);
    const attachments = useMemo(() => {
        const m = new Map();
        (plan?.attachments_plan || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan?.attachments_plan]);
    const cuts = useMemo(() => {
        const m = new Map();
        (plan?.precision_cuts || []).forEach((row) => {
            if (row?.tooth) m.set(String(row.tooth), row);
        });
        return m;
    }, [plan?.precision_cuts]);
    const biteRampTeeth = useMemo(() => {
        const br = plan?.bite_ramps || {};
        if (!br.enabled) return new Set();
        if (Array.isArray(br.teeth) && br.teeth.length > 0) return new Set(br.teeth.map(String));
        const pos = br.position || "upper_anterior";
        if (pos === "upper_anterior") return new Set(["13", "12", "11", "21", "22", "23"]);
        if (pos === "lower_anterior") return new Set(["43", "42", "41", "31", "32", "33"]);
        if (pos === "upper_canine") return new Set(["13", "23"]);
        return new Set();
    }, [plan?.bite_ramps]);
    const empty = movements.size === 0 && attachments.size === 0 && cuts.size === 0 && biteRampTeeth.size === 0;
    if (empty) {
        return (
            <div className="text-[12.5px] text-muted-foreground italic">
                Planner did not author per-tooth data for this case.
            </div>
        );
    }
    return (
        <PerToothTabs
            movements={movements}
            attachments={attachments}
            cuts={cuts}
            biteRampTeeth={biteRampTeeth}
            biteRamps={plan?.bite_ramps || {}}
        />
    );
}


/* Render JUST one of the per-tooth categories (no tab nav). Used by
   the Request Revision page where each category gets its own section
   instead of a combined 4-tab UI.
   `category`: "movements" | "attachments" | "cuts" | "biteramps".  */
export function PerToothCategoryView({ plan, category, compact = false }) {
    const movements = useMemo(() => {
        const m = new Map();
        (plan?.tooth_movements_plan || []).forEach((row) => { if (row?.tooth) m.set(String(row.tooth), row); });
        return m;
    }, [plan?.tooth_movements_plan]);
    const attachments = useMemo(() => {
        const m = new Map();
        (plan?.attachments_plan || []).forEach((row) => { if (row?.tooth) m.set(String(row.tooth), row); });
        return m;
    }, [plan?.attachments_plan]);
    const cuts = useMemo(() => {
        const m = new Map();
        (plan?.precision_cuts || []).forEach((row) => { if (row?.tooth) m.set(String(row.tooth), row); });
        return m;
    }, [plan?.precision_cuts]);
    const biteRampTeeth = useMemo(() => {
        const br = plan?.bite_ramps || {};
        if (!br.enabled) return new Set();
        if (Array.isArray(br.teeth) && br.teeth.length > 0) return new Set(br.teeth.map(String));
        const pos = br.position || "upper_anterior";
        if (pos === "upper_anterior") return new Set(["13", "12", "11", "21", "22", "23"]);
        if (pos === "lower_anterior") return new Set(["43", "42", "41", "31", "32", "33"]);
        if (pos === "upper_canine") return new Set(["13", "23"]);
        return new Set();
    }, [plan?.bite_ramps]);

    const [labelMode] = useState("fdi");

    let body, empty = false, label;
    if (category === "movements") {
        empty = movements.size === 0;
        label = "tooth movements";
        body = <PerToothMovementsTab data={movements} labelMode={labelMode} compact={compact} />;
    } else if (category === "attachments") {
        empty = attachments.size === 0;
        label = "attachments";
        body = <PerToothAttachmentsTab data={attachments} labelMode={labelMode} compact={compact} />;
    } else if (category === "cuts") {
        empty = cuts.size === 0;
        label = "precision cuts";
        body = <PerToothCutsTab data={cuts} labelMode={labelMode} compact={compact} />;
    } else if (category === "biteramps") {
        empty = biteRampTeeth.size === 0;
        label = "bite ramps";
        body = <PerToothBiteRampsTab teeth={biteRampTeeth} biteRamps={plan?.bite_ramps || {}} labelMode={labelMode} compact={compact} />;
    }

    if (empty) {
        return (
            <div className="text-[12.5px] text-muted-foreground italic">
                Planner did not author any {label} for this case.
            </div>
        );
    }

    return (
        <div data-testid={`per-tooth-category-${category}`}>
            {/* iter98.99bk — FDI/Patient toggle removed from this view
                per user direction (2026-02-19). The Request-Revision
                surface only ever uses FDI notation. */}
            {body}
        </div>
    );
}


/* Mirror chart only (no authored summary) — used on the doctor's
   revision side so they see the same tooth chart as the planner while
   typing the per-category revision note below. */
export function PerToothChartOnly({ plan, category, compact = true }) {
    const teeth = useMemo(() => {
        if (category === "movements") {
            const m = new Set();
            (plan?.tooth_movements_plan || []).forEach((row) => { if (row?.tooth) m.add(String(row.tooth)); });
            return m;
        }
        if (category === "attachments") {
            const m = new Set();
            (plan?.attachments_plan || []).forEach((row) => { if (row?.tooth) m.add(String(row.tooth)); });
            return m;
        }
        if (category === "cuts") {
            const m = new Set();
            (plan?.precision_cuts || []).forEach((row) => { if (row?.tooth) m.add(String(row.tooth)); });
            return m;
        }
        // biteramps
        const br = plan?.bite_ramps || {};
        if (!br.enabled) return new Set();
        if (Array.isArray(br.teeth) && br.teeth.length > 0) return new Set(br.teeth.map(String));
        const pos = br.position || "upper_anterior";
        if (pos === "upper_anterior") return new Set(["13", "12", "11", "21", "22", "23"]);
        if (pos === "lower_anterior") return new Set(["43", "42", "41", "31", "32", "33"]);
        if (pos === "upper_canine") return new Set(["13", "23"]);
        return new Set();
    }, [plan, category]);
    const theme = TAB_THEMES[category] || TAB_THEMES.movements;
    if (teeth.size === 0) return null;
    /* Match the chart heading used on the planner-left view so the
       Request Revision page reads as a symmetric mirror. */
    const heading = {
        movements:   "Tooth movements chart",
        attachments: "Attachment placement chart",
        cuts:        "Precision cuts placement chart",
        biteramps:   "Bite ramp placement chart",
    }[category] || "Tooth chart";
    return (
        <>
            <FieldLabel>{heading}</FieldLabel>
            <ToothChartGrid
                activeTeeth={teeth}
                theme={theme}
                testId={`revision-per-tooth-mirror-${category}`}
                compact={compact}
            />
        </>
    );
}
